/**
 * CustomVizBuilderModal — Main modal for building custom visualizations.
 * DJs compose visuals by stacking effect layers, configuring per-layer settings,
 * and mapping audio reactivity — all with a live preview.
 */

import { useState, useCallback, useRef } from 'react';
import { X, Save, Trash2, Layers, Sliders, Music } from 'lucide-react';
import toast from 'react-hot-toast';
import { CustomVisualizationPreset, CustomLayerDefinition, LayerType, AudioReactivityMapping } from '../types';
import { getDefaultSettings, getLayerSchema } from '../layerSchemas';
import { saveCustomPreset, deleteCustomPreset, loadCustomPresets } from '../storage';
import BuilderPreview from './BuilderPreview';
import LayerList from './LayerList';
import LayerTypePicker from './LayerTypePicker';
import LayerSettings from './LayerSettings';

function generateId(): string {
  return Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
}

function createEmptyPreset(): CustomVisualizationPreset {
  return {
    id: generateId(),
    name: 'My Custom Viz',
    description: '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    layers: [],
    globalSettings: {
      trailAmount: 0.1,
      beatFlash: true,
      beatFlashIntensity: 0.5,
    },
  };
}

interface CustomVizBuilderModalProps {
  /** If editing an existing preset, pass it here */
  editPreset?: CustomVisualizationPreset;
  onClose: () => void;
  /** Called after saving so the parent can refresh the visualization list */
  onSaved: () => void;
}

export default function CustomVizBuilderModal({ editPreset, onClose, onSaved }: CustomVizBuilderModalProps) {
  const [preset, setPreset] = useState<CustomVisualizationPreset>(
    editPreset ? { ...editPreset, layers: editPreset.layers.map(l => ({ ...l })) } : createEmptyPreset()
  );
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [showLayerPicker, setShowLayerPicker] = useState(false);
  const [showLoadMenu, setShowLoadMenu] = useState(false);
  const [version, setVersion] = useState(0); // bump to force preview re-init

  // Mutable ref so the preview can read latest preset without re-init
  const presetRef = useRef(preset);
  presetRef.current = preset;

  const selectedLayer = preset.layers.find(l => l.id === selectedLayerId) || null;

  // ── Layer operations ─────────────────────────────────────────────────────

  const addLayer = useCallback((type: LayerType) => {
    const schema = getLayerSchema(type);
    const newLayer: CustomLayerDefinition = {
      id: generateId(),
      type,
      label: schema?.label || type,
      visible: true,
      opacity: 1,
      blendMode: 'normal',
      settings: getDefaultSettings(type),
      reactivity: {},
    };

    setPreset(prev => {
      const updated = { ...prev, layers: [...prev.layers, newLayer] };
      presetRef.current = updated;
      return updated;
    });
    setSelectedLayerId(newLayer.id);
    setVersion(v => v + 1);
  }, []);

  const reorderLayers = useCallback((layers: CustomLayerDefinition[]) => {
    setPreset(prev => {
      const updated = { ...prev, layers };
      presetRef.current = updated;
      return updated;
    });
    setVersion(v => v + 1);
  }, []);

  const toggleVisibility = useCallback((id: string) => {
    setPreset(prev => {
      const updated = {
        ...prev,
        layers: prev.layers.map(l => l.id === id ? { ...l, visible: !l.visible } : l),
      };
      presetRef.current = updated;
      return updated;
    });
  }, []);

  const deleteLayer = useCallback((id: string) => {
    setPreset(prev => {
      const updated = { ...prev, layers: prev.layers.filter(l => l.id !== id) };
      presetRef.current = updated;
      return updated;
    });
    if (selectedLayerId === id) setSelectedLayerId(null);
    setVersion(v => v + 1);
  }, [selectedLayerId]);

  // ── Layer setting updates ────────────────────────────────────────────────

  const updateLayerSetting = useCallback((key: string, value: number | string | boolean) => {
    if (!selectedLayerId) return;
    setPreset(prev => {
      const updated = {
        ...prev,
        layers: prev.layers.map(l =>
          l.id === selectedLayerId
            ? { ...l, settings: { ...l.settings, [key]: value } }
            : l
        ),
      };
      presetRef.current = updated;
      return updated;
    });
  }, [selectedLayerId]);

  const updateLayerOpacity = useCallback((opacity: number) => {
    if (!selectedLayerId) return;
    setPreset(prev => {
      const updated = {
        ...prev,
        layers: prev.layers.map(l => l.id === selectedLayerId ? { ...l, opacity } : l),
      };
      presetRef.current = updated;
      return updated;
    });
  }, [selectedLayerId]);

  const updateLayerBlendMode = useCallback((blendMode: 'normal' | 'additive' | 'multiply') => {
    if (!selectedLayerId) return;
    setPreset(prev => {
      const updated = {
        ...prev,
        layers: prev.layers.map(l => l.id === selectedLayerId ? { ...l, blendMode } : l),
      };
      presetRef.current = updated;
      return updated;
    });
  }, [selectedLayerId]);

  const updateLayerLabel = useCallback((label: string) => {
    if (!selectedLayerId) return;
    setPreset(prev => {
      const updated = {
        ...prev,
        layers: prev.layers.map(l => l.id === selectedLayerId ? { ...l, label } : l),
      };
      presetRef.current = updated;
      return updated;
    });
  }, [selectedLayerId]);

  const updateReactivity = useCallback((key: string, source: string, intensity: number, smoothing: number) => {
    if (!selectedLayerId) return;
    setPreset(prev => {
      const mapping: AudioReactivityMapping = {
        source: source as AudioReactivityMapping['source'],
        intensity,
        smoothing,
      };
      const updated = {
        ...prev,
        layers: prev.layers.map(l =>
          l.id === selectedLayerId
            ? { ...l, reactivity: { ...l.reactivity, [key]: mapping } }
            : l
        ),
      };
      presetRef.current = updated;
      return updated;
    });
  }, [selectedLayerId]);

  // ── Global settings ─────────────────────────────────────────────────────

  const updateGlobal = useCallback((key: string, value: number | boolean) => {
    setPreset(prev => {
      const updated = { ...prev, globalSettings: { ...prev.globalSettings, [key]: value } };
      presetRef.current = updated;
      return updated;
    });
  }, []);

  // ── Save / Load / Delete ────────────────────────────────────────────────

  const handleSave = () => {
    if (!preset.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    saveCustomPreset(preset);
    onSaved();
    toast.success(`Saved "${preset.name}"`);
  };

  const handleDelete = () => {
    if (!editPreset) return;
    deleteCustomPreset(editPreset.id);
    onSaved();
    onClose();
    toast.success('Deleted custom visualization');
  };

  const handleLoad = (p: CustomVisualizationPreset) => {
    setPreset({ ...p, layers: p.layers.map(l => ({ ...l })) });
    presetRef.current = p;
    setSelectedLayerId(null);
    setShowLoadMenu(false);
    setVersion(v => v + 1);
  };

  const savedPresets = loadCustomPresets();

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-gray-900 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-gray-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-white">Custom Visualization Builder</h2>
            <input
              type="text"
              value={preset.name}
              onChange={e => setPreset(prev => ({ ...prev, name: e.target.value }))}
              className="bg-gray-800 rounded px-2 py-0.5 text-xs text-white border border-gray-700 focus:border-purple-500 outline-none w-44"
              placeholder="Visualization name..."
            />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Top row: Preview + Layer List */}
          <div className="flex gap-3" style={{ minHeight: '200px' }}>
            {/* Live preview */}
            <div className="flex-1 min-w-0 relative">
              <BuilderPreview preset={presetRef.current} version={version} />
              {/* Empty state overlay on preview */}
              {preset.layers.length === 0 && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 rounded-lg pointer-events-none">
                  <Layers className="w-8 h-8 text-gray-600 mb-2" />
                  <p className="text-sm text-gray-400 font-medium">Your canvas is empty</p>
                  <p className="text-xs text-gray-500 mt-1 text-center px-4">
                    Add a background layer first, then stack effects like bars, particles, or waveforms on top.
                  </p>
                </div>
              )}
            </div>

            {/* Layer list */}
            <div className="w-52 shrink-0">
              <LayerList
                layers={preset.layers}
                selectedId={selectedLayerId}
                onSelect={setSelectedLayerId}
                onReorder={reorderLayers}
                onToggleVisibility={toggleVisibility}
                onDelete={deleteLayer}
                onAddClick={() => setShowLayerPicker(true)}
              />
            </div>
          </div>

          {/* Getting started hints (shown when there are layers but none selected) */}
          {preset.layers.length > 0 && !selectedLayer && (
            <div className="bg-gray-800/20 rounded-lg p-3 border border-gray-800/50 flex items-start gap-3">
              <Sliders className="w-4 h-4 text-purple-400 mt-0.5 shrink-0" />
              <div className="text-xs text-gray-400">
                <span className="text-gray-300 font-medium">Click a layer</span> to adjust its settings.
                Use the <span className="text-gray-300">Audio Reactivity</span> section to make properties respond to bass, treble, or beat detection.
              </div>
            </div>
          )}

          {/* Layer settings (shown when a layer is selected) */}
          {selectedLayer && (
            <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-800">
              <LayerSettings
                layer={selectedLayer}
                onUpdateSetting={updateLayerSetting}
                onUpdateOpacity={updateLayerOpacity}
                onUpdateBlendMode={updateLayerBlendMode}
                onUpdateLabel={updateLayerLabel}
                onUpdateReactivity={updateReactivity}
              />

              {/* Hint: audio reactivity tip when no mappings are set */}
              {Object.values(selectedLayer.reactivity).every(m => m.source === 'none') && (
                <div className="mt-3 flex items-start gap-2 bg-purple-500/5 rounded-lg p-2 border border-purple-500/10">
                  <Music className="w-3.5 h-3.5 text-purple-400 mt-0.5 shrink-0" />
                  <p className="text-[10px] text-purple-300/70 leading-relaxed">
                    Tip: Use the Audio Reactivity dropdowns above to make this layer react to music.
                    Try mapping <span className="text-purple-300">Color Shift</span> to <span className="text-purple-300">Bass</span> for a dynamic color effect.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Global settings */}
          <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-800">
            <label className="text-[10px] text-gray-400 uppercase tracking-wider block mb-2">Global Settings</label>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[140px]">
                <label className="text-[10px] text-gray-400 block mb-1">
                  Trail: {preset.globalSettings.trailAmount.toFixed(2)}
                </label>
                <input
                  type="range" min={0} max={0.98} step={0.02}
                  value={preset.globalSettings.trailAmount}
                  onChange={e => updateGlobal('trailAmount', parseFloat(e.target.value))}
                  className="w-full accent-purple-500"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className="text-[10px] text-gray-400 block mb-1">
                  Beat Flash: {preset.globalSettings.beatFlashIntensity.toFixed(1)}
                </label>
                <input
                  type="range" min={0} max={1} step={0.1}
                  value={preset.globalSettings.beatFlashIntensity}
                  onChange={e => updateGlobal('beatFlashIntensity', parseFloat(e.target.value))}
                  className="w-full accent-purple-500"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preset.globalSettings.beatFlash}
                  onChange={e => updateGlobal('beatFlash', e.target.checked)}
                  className="rounded accent-purple-500"
                />
                Beat Flash
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800">
          <div className="flex gap-2">
            {/* Load menu */}
            <div className="relative">
              <button
                onClick={() => setShowLoadMenu(!showLoadMenu)}
                disabled={savedPresets.length === 0}
                className="px-3 py-1.5 rounded-lg text-xs bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Load ({savedPresets.length})
              </button>
              {showLoadMenu && savedPresets.length > 0 && (
                <div className="absolute bottom-full mb-1 left-0 bg-gray-800 rounded-lg border border-gray-700 py-1 min-w-[180px] z-10">
                  {savedPresets.map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleLoad(p)}
                      className="block w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700"
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {editPreset && (
              <button
                onClick={handleDelete}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-red-900/30 text-red-400 hover:bg-red-900/50"
              >
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>

          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs bg-purple-600 text-white hover:bg-purple-500 font-medium"
          >
            <Save size={14} /> Save
          </button>
        </div>
      </div>

      {/* Layer type picker overlay */}
      {showLayerPicker && (
        <LayerTypePicker
          onSelect={addLayer}
          onClose={() => setShowLayerPicker(false)}
        />
      )}
    </div>
  );
}
